import { useEffect, useState } from 'react';
import {
	PolicyStatus,
	PolicyStatusSchema,
	update,
} from '@/api/policy-status/policy-status';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { databaseInstances } from '@/constants/database-instances';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdatePolicyStatus({
	policyStatus,
}: {
	policyStatus: PolicyStatus;
}) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (updatedPolicyStatus: PolicyStatus) =>
			update(updatedPolicyStatus),
		onSuccess: () => {
			toast.success(`Success! Status has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: [`policy-status`],
			});
		},
		onError: () => {
			toast.error(`Error! Status could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<PolicyStatus>({
		resolver: zodResolver(PolicyStatusSchema),
		defaultValues: policyStatus,
		mode: 'all',
	});

	const { reset } = form;
	useEffect(() => {
		reset(policyStatus);
	}, [policyStatus, reset]);

	const onSubmit: SubmitHandler<PolicyStatus> = (values: PolicyStatus) =>
		mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update Status</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Update Status</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-2">
								<FormField
									control={form.control}
									name="amsStatus"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												AMS Status<span className="text-red-500">*</span>
											</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="mappedStatus"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												Mapped Status<span className="text-red-500">*</span>
											</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="description"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												Description<span className="text-red-500">*</span>
											</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="databaseInstanceId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Database Instance</FormLabel>
											<FormControl>
												<Select
													value={field.value?.toString() ?? '0'}
													onValueChange={(val) => {
														const numValue = parseInt(val, 10);
														field.onChange(isNaN(numValue) ? 0 : numValue);
													}}
												>
													<SelectTrigger>
														<SelectValue placeholder="Select a field..." />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															<SelectLabel>Database Instances</SelectLabel>
															{databaseInstances!.map((field) => (
																<SelectItem key={field.id} value={field.id.toString()}>
																	{field.name}
																</SelectItem>
															))}
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="isPrimaryStatus"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
											<div className="space-y-0.5">
												<FormLabel>Primary Status</FormLabel>
											</div>
											<FormControl>
												<Switch checked={!!field.value} onCheckedChange={field.onChange} />
											</FormControl>
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="isExpired"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
											<div className="space-y-0.5">
												<FormLabel>Expired</FormLabel>
											</div>
											<FormControl>
												<Switch checked={!!field.value} onCheckedChange={field.onChange} />
											</FormControl>
										</FormItem>
									)}
								/>
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Updating...' : 'Update'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
